import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, type RouteObject, useRoutes, useLocation } from 'react-router';
import { Guard } from '@/modules/shared/components/guard';
import { useAuth } from '@/modules/shared/components/auth-provider';
import { GhibliBackground } from '@/modules/shared/components/ghibli-background';
import { GhibliLoader } from '@/modules/shared/components/ghibli-loader';
import { Header } from '@/modules/shared/components/header';
import { RouteProvider } from '@/modules/shared/components/route-provider';

import coreRoutes from '@/modules/core/routes';
import superRoutes from '@/modules/super/routes';
import admRoutes from '@/modules/adm/routes';
import professorRoutes from '@/modules/professor/routes';
import responsavelRoutes from '@/modules/responsavel/routes';
import alunoRoutes from '@/modules/aluno/routes';

const SuperHomeLazy = lazy(() => import('@/modules/super/pages/home'));
const AdmHomeLazy = lazy(() => import('@/modules/adm/pages/home'));
const ProfessorHomeLazy = lazy(() => import('@/modules/professor/pages/home'));
const ResponsavelHomeLazy = lazy(() => import('@/modules/responsavel/pages/home'));
const AlunoHomeLazy = lazy(() => import('@/modules/aluno/pages/home'));

const HomeDispatcher = () => {
  const { identity } = useAuth();
  const location = useLocation();
  const selectedRole = identity?.selected_role || identity?.role;

  if (!identity || !selectedRole) {
    if (location.pathname !== '/login') {
      return <Navigate to="/login" replace />;
    }
    return null;
  }

  if (selectedRole === 'SUPER' && !identity.school_id) {
    return (
      <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
        <SuperHomeLazy />
      </Suspense>
    );
  }

  const precisaSelecionarPerfil =
    (selectedRole === 'RESPONSAVEL' || selectedRole === 'ALUNO') &&
    !identity.selected_id;
  if (precisaSelecionarPerfil) {
    return <Navigate to="/profile" replace />;
  }

  switch (selectedRole) {
    case 'SUPER':
      return (
        <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
          <Guard allowedRoles={['SUPER']} allowSuperWithoutSchool>
            <SuperHomeLazy />
          </Guard>
        </Suspense>
      );
    case 'ADM':
      return (
        <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
          <Guard allowedRoles={['ADM', 'SUPER']}>
            <AdmHomeLazy />
          </Guard>
        </Suspense>
      );
    case 'PROFESSOR':
      return (
        <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
          <Guard allowedRoles={['PROFESSOR', 'SUPER', 'ADM']}>
            <ProfessorHomeLazy />
          </Guard>
        </Suspense>
      );
    case 'RESPONSAVEL':
      return (
        <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
          <Guard allowedRoles={['RESPONSAVEL', 'SUPER', 'ADM']}>
            <ResponsavelHomeLazy />
          </Guard>
        </Suspense>
      );
    case 'ALUNO':
      return (
        <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
          <Guard allowedRoles={['ALUNO', 'SUPER', 'ADM', 'PROFESSOR', 'RESPONSAVEL']}>
            <AlunoHomeLazy />
          </Guard>
        </Suspense>
      );
    default:
      return <Navigate to="/login" replace />;
  }
};

const filterOutRootRoute = (routes: RouteObject[]): RouteObject[] =>
  routes.filter((r) => r.path !== '/');

const allModuleRoutes: RouteObject[] = [
  ...coreRoutes,
  ...filterOutRootRoute(superRoutes),
  ...filterOutRootRoute(admRoutes),
  ...filterOutRootRoute(professorRoutes),
  ...filterOutRootRoute(responsavelRoutes),
  ...filterOutRootRoute(alunoRoutes),
];

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomeDispatcher />,
    handle: { crumb: 'Home' }
  },
  ...allModuleRoutes,
  {
    path: '*',
    element: <Navigate to="/" replace />
  }
];

const PageShell = ({ children }: { children: ReactNode }) => {
  const { identity } = useAuth();

  if (!identity || !identity.role) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      <GhibliBackground />
      <div className="relative z-10 flex flex-col">
        <Header />
        <div className="grow w-full px-4 md:px-8 py-4 md:py-6">
          {children}
        </div>
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const element = useRoutes(routes);
  return element;
};

export function Router() {
  const { identity } = useAuth();
  const location = useLocation();

  if (!identity || !identity.role) {
    if (location.pathname === '/login') {
      return (
        <RouteProvider routes={routes}>
          <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
            <AppRoutes />
          </Suspense>
        </RouteProvider>
      );
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <RouteProvider routes={routes}>
      <Suspense fallback={<GhibliLoader className="min-h-screen" />}>
        <PageShell>
          <AppRoutes />
        </PageShell>
      </Suspense>
    </RouteProvider>
  );
}
